import { useEffect, useRef, useState } from 'react';
import { Alert, App, Button, Form, Input, Modal, Select, Space, Typography } from 'antd';
import type { AssignPackRequest, PackAssignment } from '@goelectric/shared-types';
import { createIdempotencyKey } from '../../api/customerApi';
import { asignacionesApi } from '../api/asignacionesApi';
import { useAssignablePacks } from '../hooks/useAssignableCatalog';
import { describeAssignmentError } from '../utils/errors';
import { formatCop, formatKwh, formatVigencia } from '../utils/format';

interface AssignPackFormValues {
  packId: string;
  note?: string;
}

export interface AssignPackModalProps {
  open: boolean;
  customerId: string;
  customerName?: string;
  onClose: () => void;
  /** Refresca la ficha tras asignar. */
  onAssigned: (assignment: PackAssignment) => void;
}

/**
 * Modal **Asignar pack** (US-2.4, TG-53).
 *
 * Carga el catálogo `ACTIVE` vigente (`GET /api/v1/packs?status=ACTIVE&validAt=...`)
 * y envía `POST .../customers/{id}/packs` con `Idempotency-Key`. La clave se genera
 * al abrir el modal y se reutiliza en reintentos del mismo envío; el backend valida
 * de nuevo y puede responder `PACK_NOT_ASSIGNABLE` si el pack cambió de estado.
 */
export function AssignPackModal({
  open,
  customerId,
  customerName,
  onClose,
  onAssigned,
}: AssignPackModalProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm<AssignPackFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const catalog = useAssignablePacks(open);
  const idempotencyKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (open) {
      setError(null);
      form.resetFields();
      idempotencyKeyRef.current = createIdempotencyKey();
    } else {
      idempotencyKeyRef.current = null;
    }
  }, [open, form]);

  const handleCancel = () => {
    idempotencyKeyRef.current = null;
    onClose();
  };

  const handleFinish = async (values: AssignPackFormValues) => {
    setSubmitting(true);
    setError(null);
    const body: AssignPackRequest = {
      packId: values.packId,
      note: values.note?.trim() || undefined,
    };
    try {
      const idempotencyKey =
        idempotencyKeyRef.current ?? (idempotencyKeyRef.current = createIdempotencyKey());
      const created = await asignacionesApi.assignPack(customerId, body, idempotencyKey);
      idempotencyKeyRef.current = null;
      message.success(`Pack asignado${customerName ? ` a ${customerName}` : ''}.`);
      onAssigned(created);
      onClose();
    } catch (caught) {
      setError(describeAssignmentError(caught, 'No se pudo asignar el pack.'));
    } finally {
      setSubmitting(false);
    }
  };

  const options = catalog.items.map((pack) => ({
    value: pack.id,
    label: `${pack.name} — ${formatKwh(pack.kwh)} — ${formatCop(pack.priceCop, pack.currency || 'COP')}`,
  }));

  return (
    <Modal
      open={open}
      title="Asignar pack"
      onCancel={handleCancel}
      onOk={() => form.submit()}
      okText="Asignar pack"
      cancelText="Cancelar"
      confirmLoading={submitting}
      destroyOnHidden
      keyboard
    >
      {error && (
        <Alert
          type="error"
          showIcon
          role="alert"
          message="No se pudo asignar el pack"
          description={error}
          style={{ marginBottom: 16 }}
        />
      )}

      <Typography.Paragraph>
        Solo se ofrecen packs <strong>activos y vigentes</strong>. La asignación es
        administrativa y no debita el saldo del cliente.
      </Typography.Paragraph>

      {catalog.error && (
        <Alert
          type="error"
          showIcon
          message="No se pudo cargar el catálogo de packs"
          description={catalog.error}
          action={
            <Button size="small" onClick={catalog.refresh}>
              Reintentar
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {!catalog.loading && !catalog.error && options.length === 0 && (
        <Alert
          type="info"
          showIcon
          message="No hay packs asignables"
          description="Crea un pack activo y vigente en el catálogo antes de asignarlo a un cliente."
          style={{ marginBottom: 16 }}
        />
      )}

      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          label="Pack"
          name="packId"
          rules={[{ required: true, message: 'Selecciona el pack a asignar.' }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            loading={catalog.loading}
            options={options}
            placeholder="Selecciona un pack activo y vigente"
            aria-label="Pack a asignar"
            notFoundContent={catalog.loading ? 'Cargando packs…' : 'Sin packs asignables'}
          />
        </Form.Item>

        <Form.Item label="Nota (interna)" name="note">
          <Input.TextArea
            rows={3}
            maxLength={500}
            placeholder="Motivo o referencia de la asignación (visible en auditoría)."
          />
        </Form.Item>
      </Form>

      <Space direction="vertical" size={2}>
        {catalog.items.length > 0 && (
          <Typography.Text>
            Vigencia de referencia: {formatVigencia(catalog.items[0]?.validFrom, catalog.items[0]?.validTo)}
          </Typography.Text>
        )}
      </Space>
    </Modal>
  );
}
